import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CreditCard, Lock, Crown, Check, AlertCircle, ArrowLeft, Gift, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);

export default function MembershipCheckout() {
  const [params] = useSearchParams();
  const planCode = params.get("plan") || "premium";
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [planDef, setPlanDef] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Referral code state
  const [refCode, setRefCode] = useState(params.get("ref") || "");
  const [refValidation, setRefValidation] = useState(null); // {valid, message, discount_pct, referrer_first_name} | null
  const [refValidating, setRefValidating] = useState(false);
  const [refError, setRefError] = useState(null);

  // Mock form state — simulated checkout
  const [card, setCard] = useState({ number: "4242 4242 4242 4242", holder: "", exp: "12/28", cvc: "123" });

  useEffect(() => {
    api.get("/plans").then((r) => {
      const p = r.data.plans.find((x) => x.code === planCode);
      setPlanDef(p);
    });
  }, [planCode]);

  const validateCode = async () => {
    if (!refCode.trim()) return;
    setRefValidating(true); setRefError(null); setRefValidation(null);
    try {
      const { data } = await api.get(`/referrals/validate?code=${encodeURIComponent(refCode.trim().toUpperCase())}`);
      setRefValidation(data);
    } catch (e) {
      setRefError(e.response?.data?.detail || "Código no válido");
    } finally {
      setRefValidating(false);
    }
  };

  const clearCode = () => {
    setRefCode("");
    setRefValidation(null);
    setRefError(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const body = { plan: planCode };
      if (refValidation?.valid && refCode.trim()) {
        body.referral_code = refCode.trim().toUpperCase();
      }
      const { data } = await api.post("/membership/checkout", body);
      setSuccess(data);
      await refresh();
      setTimeout(() => nav("/app/membresia"), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo procesar el pago");
    } finally {
      setLoading(false);
    }
  };

  if (!planDef) return <div className="container-app py-16"><p className="text-muted-foreground">Cargando…</p></div>;

  if (success) {
    return (
      <div className="container-app py-20 max-w-xl text-center" data-testid="checkout-success">
        <div className="panel p-10 ring-2 ring-emerald-400/30">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-400/20 flex items-center justify-center">
            <Check size={28} className="text-emerald-400" />
          </div>
          <h1 className="serif text-3xl mt-6">¡Pago confirmado!</h1>
          <p className="mt-3 text-muted-foreground">{success.message}</p>
          {success.payment?.founder_number && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/15 border border-amber-400/40">
              <Crown size={16} className="text-amber-400" />
              <span className="text-amber-300 font-bold tracking-wider">FUNDADOR N°{success.payment.founder_number}</span>
            </div>
          )}
          <p className="mt-6 text-xs text-muted-foreground">Redirigiendo a tu panel de membresía…</p>
        </div>
      </div>
    );
  }

  const isFree = planCode === "free";
  const isFounder = planCode === "founder";

  return (
    <div className="container-app py-12 max-w-4xl" data-testid="checkout-page">
      <Link to="/planes" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-6">
        <ArrowLeft size={14} /> Volver a planes
      </Link>
      <div className="grid md:grid-cols-5 gap-8">
        {/* Summary */}
        <aside className="md:col-span-2">
          <div className={`panel p-6 ${isFounder ? "ring-2 ring-amber-400/30" : ""}`}>
            {isFounder && <div className="inline-flex items-center gap-1.5 mb-2 text-xs text-amber-300"><Crown size={14}/>Cupo limitado</div>}
            <div className="label-eyebrow">Resumen</div>
            <h2 className="serif text-2xl mt-2">{planDef.name}</h2>
            <div className="serif text-4xl mt-3">{isFree ? "Gratis" : CLP(planDef.price_clp)}{!isFree && <span className="text-sm text-muted-foreground font-sans"> / mes</span>}</div>
            <div className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Comisión</span><span>{planDef.commission_pct}%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Delegaciones gratis</span><span>{planDef.free_delegations}/mes</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Firmas e. simples</span><span>{planDef.signatures}/mes</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Prioridad alerta</span><span className="text-right">{planDef.priority_label}</span></div>
              {refValidation?.valid && !isFree && (
                <>
                  <div className="flex justify-between text-amber-300" data-testid="discount-row">
                    <span>Descuento ({refValidation.discount_pct}%)</span>
                    <span>− {CLP(Math.round(planDef.price_clp * refValidation.discount_pct / 100))}</span>
                  </div>
                </>
              )}
            </div>
            <div className="mt-5 pt-5 border-t border-white/5 flex justify-between text-sm">
              <span className="text-muted-foreground">Total hoy</span>
              <span className="serif text-lg" data-testid="checkout-total">
                {isFree ? "—" : (
                  refValidation?.valid
                    ? CLP(planDef.price_clp - Math.round(planDef.price_clp * refValidation.discount_pct / 100))
                    : CLP(planDef.price_clp)
                )}
              </span>
            </div>
            {refValidation?.valid && !isFree && (
              <p className="mt-3 text-[11px] text-amber-300/80">
                {refValidation.discount_pct}% OFF aplicado en tu primer mes. Renueva al precio normal el próximo período.
              </p>
            )}
          </div>

          <div className="mt-4 panel p-4 text-xs text-muted-foreground flex items-start gap-2">
            <Lock size={14} className="mt-0.5 text-[hsl(var(--accent))]" />
            <div>
              Pago simulado · sin cobro real. En producción se integra <span className="text-foreground">Flow.cl / Webpay Plus</span> con cobro recurrente.
            </div>
          </div>
        </aside>

        {/* Form */}
        <form onSubmit={submit} className="md:col-span-3 panel p-8" data-testid="checkout-form">
          <h1 className="serif text-2xl">{isFree ? "Confirmar plan gratuito" : "Datos de pago"}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isFree
              ? "El plan Free no requiere pago. Puedes mejorar tu plan cuando quieras."
              : "Ingresa los datos de tu tarjeta. Es una simulación — usa cualquier valor."}
          </p>

          {!isFree && (
            <div className="mt-6 space-y-4">
              {/* Referral code section */}
              <div className="panel p-4 bg-amber-400/5 border border-amber-400/20" data-testid="referral-section">
                <div className="flex items-center gap-2 mb-2">
                  <Gift size={14} className="text-amber-400" />
                  <span className="text-sm font-medium">¿Tienes un código de invitación de un Fundador?</span>
                </div>
                {!refValidation?.valid ? (
                  <div className="flex gap-2">
                    <input
                      value={refCode}
                      onChange={(e) => { setRefCode(e.target.value.toUpperCase()); setRefError(null); }}
                      className="flex-1 px-3 py-2 rounded-md border border-white/10 bg-white/5 outline-none text-sm font-mono"
                      placeholder="AMIGO-N1-XXXX"
                      data-testid="referral-input"
                    />
                    <button
                      type="button"
                      onClick={validateCode}
                      disabled={refValidating || !refCode.trim()}
                      className="btn-ghost text-sm px-4 disabled:opacity-50"
                      data-testid="referral-apply"
                    >
                      {refValidating ? "…" : "Aplicar"}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2" data-testid="referral-applied">
                    <div className="flex items-center gap-2 text-sm text-amber-300">
                      <Check size={14} />
                      <span>{refValidation.message}</span>
                    </div>
                    <button
                      type="button"
                      onClick={clearCode}
                      className="shrink-0 px-2.5 py-1 rounded-md border border-white/10 hover:border-amber-400/40 hover:bg-amber-400/10 text-xs inline-flex items-center gap-1.5 transition-colors"
                      data-testid="referral-clear"
                      title="Quitar código"
                    >
                      <X size={12} /> Quitar
                    </button>
                  </div>
                )}
                {refError && (
                  <p className="mt-2 text-xs text-red-400" data-testid="referral-error">{refError}</p>
                )}
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Número de tarjeta</label>
                <div className="mt-1 flex items-center gap-2 px-3 py-2.5 rounded-md border border-white/10 bg-white/5">
                  <CreditCard size={16} className="text-muted-foreground" />
                  <input
                    value={card.number}
                    onChange={(e) => setCard({ ...card, number: e.target.value })}
                    className="bg-transparent flex-1 outline-none text-sm"
                    placeholder="4242 4242 4242 4242"
                    data-testid="checkout-card-number"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Titular</label>
                <input
                  value={card.holder}
                  onChange={(e) => setCard({ ...card, holder: e.target.value })}
                  className="mt-1 w-full px-3 py-2.5 rounded-md border border-white/10 bg-white/5 outline-none text-sm"
                  placeholder="Nombre como aparece en la tarjeta"
                  data-testid="checkout-holder"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">Vencimiento</label>
                  <input
                    value={card.exp}
                    onChange={(e) => setCard({ ...card, exp: e.target.value })}
                    className="mt-1 w-full px-3 py-2.5 rounded-md border border-white/10 bg-white/5 outline-none text-sm"
                    placeholder="MM/AA"
                    data-testid="checkout-exp"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">CVC</label>
                  <input
                    value={card.cvc}
                    onChange={(e) => setCard({ ...card, cvc: e.target.value })}
                    className="mt-1 w-full px-3 py-2.5 rounded-md border border-white/10 bg-white/5 outline-none text-sm"
                    placeholder="123"
                    data-testid="checkout-cvc"
                  />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-5 flex items-start gap-2 text-sm text-red-400 bg-red-400/10 border border-red-400/30 rounded-md p-3" data-testid="checkout-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <button
            disabled={loading}
            className={`mt-8 w-full ${isFounder ? "btn-primary bg-amber-500 hover:bg-amber-400 text-amber-950" : "btn-primary"} disabled:opacity-50`}
            data-testid="checkout-submit"
          >
            {loading ? "Procesando…" : isFree ? "Confirmar plan Free" : `Pagar ${CLP(refValidation?.valid ? planDef.price_clp - Math.round(planDef.price_clp * refValidation.discount_pct / 100) : planDef.price_clp)} y activar`}
          </button>

          <p className="mt-3 text-[11px] text-center text-muted-foreground">
            Al continuar aceptas los Términos y la Política de Privacidad. Puedes cancelar tu suscripción cuando quieras.
          </p>
        </form>
      </div>
    </div>
  );
}
